'use client';

import { cn, getColorway } from 'utils/styles';
import Details from 'components/shared/BaseAccordion/Details';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import Summary from 'components/shared/BaseAccordion/Summary';
import { SharedBentoBoxCardProps } from 'types/sharedComponents';
import { getRichTextValue } from 'utils/formatters';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { useState } from 'react';
import { BentoBoxCardHeadlineSize, BentoBoxLayout } from 'types/constants';

interface Props extends SharedBentoBoxCardProps {
  headlineSize?: BentoBoxCardHeadlineSize;
  isLargeCard?: boolean;
  layout?: BentoBoxLayout;
  mobileCopyExpanded?: boolean;
  classNames?: string;
}

const BentoBoxCard = ({
  sys,
  color,
  cardHeadlineRte,
  headlineSize,
  copy,
  isLargeCard,
  layout,
  mobileCopyExpanded,
  classNames,
}: Props) => {
  const [open, setIsOpen] = useState(false);

  const hasHeadlineAndCopy = cardHeadlineRte && copy;
  const displayMobileAccordion = hasHeadlineAndCopy && !mobileCopyExpanded;
  const labelText = cardHeadlineRte ? String(getRichTextValue(cardHeadlineRte)) : '';

  const header = (
    <div
      className={cn('font-sans font-semibold text-heading-sm', {
        'md:text-heading-md': isLargeCard || headlineSize === BentoBoxCardHeadlineSize.Large,
      })}
      {...liveUpdateAttrs(sys?.id, 'cardHeadlineRte')}>
      <RichTextCopy copy={cardHeadlineRte} />
    </div>
  );
  const body = (
    <div className="font-sans font-medium" {...liveUpdateAttrs(sys?.id, 'cardCopy')}>
      <RichTextCopy copy={copy}></RichTextCopy>
    </div>
  );

  const copyWrapper = (paddingOverride?: string) => (
    <div className={cn('pt-6', paddingOverride)} data-testid="mobile-bento-card-copy-wrapper">
      {body}
    </div>
  );

  const handleOnClick = (value: boolean) => {
    setIsOpen(value);
  };

  return (
    <div
      className={cn(
        'flex md:items-center h-full rounded-lg p-6 lg:p-12 col-span-2 sm:col-span-1 sm:aspect-3/2',
        classNames,
        layout !== BentoBoxLayout.ThreeByTwo && 'md:aspect-square',
        getColorway(color),
      )}>
      <div
        className={cn(displayMobileAccordion && 'hidden', 'sm:flex w-full flex-col sm:h-full', {
          'h-[11rem]': hasHeadlineAndCopy && !mobileCopyExpanded && !isLargeCard,
          'h-[11.5rem]': isLargeCard && !mobileCopyExpanded,
          'min-h-[11rem]': hasHeadlineAndCopy && mobileCopyExpanded && !isLargeCard,
          'min-h-[11.5rem]': isLargeCard && mobileCopyExpanded,
          'h-full': !hasHeadlineAndCopy,
        })}>
        {cardHeadlineRte && header}
        <div className="grow content-center">{copy && (mobileCopyExpanded ? copyWrapper('md:pt-0') : body)}</div>
      </div>

      {displayMobileAccordion && (
        <div className="sm:hidden block w-full" data-testid="mobile-bento-card">
          <Details open={open}>
            <Summary
              openIcon="add_circle"
              closeIcon="remove_circle"
              label={labelText}
              isActive={open}
              onClick={handleOnClick}
              data-testid="mobile-bento-card-summary">
              {header}
            </Summary>
            {copyWrapper()}
          </Details>
        </div>
      )}
    </div>
  );
};

export default BentoBoxCard;
